import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
    IconArrowUp,
    IconBriefcase,
    IconInfoCircle,
    IconMail,
    IconPhone,
    IconUser,
    IconUsers,
    IconBuildingStore,
} from '@tabler/icons-react';
import PageTitle from '../../shared/PageTitle';
import { updateTableDetailsID } from '../../../redux/TableSearch/dataSlice';
import getLabels from './arbk/labels';
import CountryFlag from './arbk/CountryFlag';
import StatusChip from './arbk/StatusChip';
import { normalizeStatus, statusMeta, displayOrDash } from './arbk/status';
import { iconForBusinessForm } from './arbk/searchConstants';

const STATUS_RANK = {
    active: 0,
    passive: 1,
    closed: 2,
    unknown: 3,
};

function sortValue(td, key) {
    if (key === 'Statusi') return STATUS_RANK[normalizeStatus(td.StatusiARBK)];
    return String(td[key] || '').toLocaleLowerCase('sq');
}

function parseNameEntry(entry) {
    const raw = String(entry || '').trim();
    if (!raw) return null;
    const match = raw.match(/,\s*([A-Za-z]{2,3})\s*$/);
    if (!match) return { name: raw, code: '' };
    return {
        name: raw.slice(0, match.index).replace(/\s+/g, ' ').trim(),
        code: match[1].toUpperCase(),
    };
}

function splitNames(raw) {
    if (!raw || !String(raw).trim()) return [];
    return String(raw)
        .split(';')
        .map((s) => s.trim())
        .filter(Boolean)
        .map(parseNameEntry)
        .filter((person) => person && person.name);
}

// `Fizik` vjen si boolean nga API-ja, por pranohen edhe 0/1 dhe "0"/"1".
// Kthen true = person juridik, false = person fizik, undefined = e panjohur.
function isLegalPerson(value) {
    if (value === false || value === 0 || value === '0' || value === 'false') return true;
    if (value === true || value === 1 || value === '1' || value === 'true') return false;
    return undefined;
}

// Shenjat e formave juridike, pa pika: "SH.P.K." -> shpk, "D.O.O." -> doo, "A.D." -> ad.
const LEGAL_FORM_TOKENS = new Set([
    'shpk', 'sha', 'ntsh', 'nsh', 'ntp', 'nt', 'np', 'ok', 'kb', 'ndermarrja', 'shoqeria',
    'doo', 'jdoo', 'ad', 'dd', 'kd', 'od', 'ood', 'eood',
    'llc', 'ltd', 'limited', 'inc', 'incorporated', 'corp', 'corporation', 'co', 'company',
    'gmbh', 'mbh', 'ug', 'ag', 'kg', 'ohg', 'se',
    'srl', 'spa', 'sarl', 'sas', 'sa', 'bv', 'nv', 'plc', 'oy', 'ab', 'aps', 'as', 'zoo',
]);

// Lista e rezultateve e ka `Pronari` vetem si tekst — ARBK-ja s'e kthen `Fizik` ne kerkim
// (vetem ne detaje). Prandaj lloji nxirret nga forma juridike ne emer:
// "BLIC SOLUTION SH.P.K." -> juridik, "Camila Parker" -> fizik.
function looksLegalByName(name) {
    const cleaned = String(name || '')
        .toLowerCase()
        .replace(/\./g, '')
        .replace(/[^a-z0-9&\s]/g, ' ')
        .trim();
    if (!cleaned) return false;
    if (cleaned.includes('&')) return true;
    return cleaned.split(/\s+/).some((token) => LEGAL_FORM_TOKENS.has(token));
}

// Kur API-ja e ofron `PronaretLista` me `Fizik`, ai eshte burimi i sakte; perndryshe
// stringu `Pronari` plus heuristika e mesiperme.
function ownerPersons(td) {
    if (!Array.isArray(td.PronaretLista)) {
        return splitNames(td.Pronari).map((person) => ({
            ...person,
            isLegal: looksLegalByName(person.name),
        }));
    }
    return td.PronaretLista
        .map((item) => {
            const parsed = parseNameEntry(item && (item.Emri ?? item.emri));
            if (!parsed) return null;
            const code = String((item && (item.CodeShteti ?? item.codeShteti)) || '')
                .trim()
                .toUpperCase();
            const fizik = isLegalPerson(item && (item.Fizik ?? item.fizik));
            return {
                ...parsed,
                code: code || parsed.code,
                isLegal: fizik === undefined ? looksLegalByName(parsed.name) : fizik,
            };
        })
        .filter((person) => person && person.name);
}

function cellTextClass(muted) {
    return muted ? 'tw-text-muted' : 'tw-text-ink';
}

function formatDate(value) {
    if (!value || !String(value).trim()) return '';
    const m = dayjs(value);
    return m.isValid() ? m.format('DD/MM/YYYY') : '';
}

const PASSIVE_DATE = formatDate('2022-06-09');

function statusDateLines(td, statusKey, labels) {
    const lines = [];
    const established = formatDate(td.DataRegjistrimit);
    lines.push({ label: labels.results.established, value: established || '—' });
    if (statusKey === 'passive') {
        lines.push({ label: labels.results.passive, value: PASSIVE_DATE });
    } else if (statusKey === 'closed') {
        const closed = formatDate(td.DataShuarjesBiznesit);
        lines.push({ label: labels.results.closed, value: closed || '—' });
    }
    return lines;
}

function unitCount(td) {
    const raw = td.NumriNjesive ?? td.NjesiteCount ?? td.Njesite ?? td.NrNjesive;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
}

const NO_BOARD_BUSINESS_TYPES = new Set(['1', '2', '3']);

function hasBoard(td) {
    const id = td.nLlojiBiznesitID == null ? '' : String(td.nLlojiBiznesitID).trim();
    return !NO_BOARD_BUSINESS_TYPES.has(id);
}

function UnitCountChip({ count, labels }) {
    if (!count) return null;
    return (
        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-h-6 tw-px-2.5 tw-rounded-full tw-bg-navy-a10 tw-text-navy tw-text-13 tw-font-semibold">
            <IconBuildingStore size={14} stroke={2} aria-hidden />
            {labels.results.units(count)}
        </span>
    );
}

const CELL_BASE = 'tw-px-4 tw-py-3.5 tw-text-sm tw-font-inter tw-align-middle tw-text-left tw-break-words';

const ACCENT_MASK = 'linear-gradient(90deg, #000 0%, #000 35%, rgba(0,0,0,0) 78%)';
function AccentOverlay({ color }) {
    return (
        <span
            aria-hidden
            style={{
                position: 'absolute',
                inset: 0,
                borderRadius: 15,
                borderStyle: 'solid',
                borderColor: color,
                borderWidth: '4px 0 4px 4px',
                WebkitMaskImage: ACCENT_MASK,
                maskImage: ACCENT_MASK,
                pointerEvents: 'none',
            }}
        />
    );
}

function GroupHeading({ children }) {
    return (
        <h3 className="tw-m-0 tw-mb-1.5 tw-text-xs tw-uppercase tw-font-semibold tw-tracking-overline tw-text-muted tw-leading-snug">
            {children}
        </h3>
    );
}

function PersonPill({ person, icon: Icon, labels }) {
    const { name, code, isLegal } = person;
    const hasType = typeof isLegal === 'boolean';
    const TypeIcon = isLegal ? IconBriefcase : IconUser;
    const typeLabel = isLegal ? labels.results.ownerLegal : labels.results.ownerPhysical;
    return (
        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-border tw-border-solid tw-border-line tw-rounded-brand tw-px-1.5 tw-py-0.5 tw-bg-paper tw-text-xs tw-font-medium tw-text-ink tw-leading-tight tw-break-words tw-min-w-0">
            {hasType && (
                <span
                    title={typeLabel}
                    aria-label={typeLabel}
                    className={`tw-inline-flex tw-flex-shrink-0 ${isLegal ? 'tw-text-st-active' : 'tw-text-navy'}`}
                >
                    <TypeIcon size={13} stroke={2} aria-hidden />
                </span>
            )}
            {code && <CountryFlag code={code} size={13} />}
            {!code && !hasType && (
                <Icon size={13} stroke={2} className="tw-text-muted tw-flex-shrink-0" aria-hidden />
            )}
            {name}
        </span>
    );
}

const MAX_VISIBLE_PERSONS = 3;

function PersonList({
    persons, icon, emptyLabel, labels,
}) {
    const [expanded, setExpanded] = useState(false);
    if (persons.length === 0) {
        return <span className="tw-text-sm tw-text-muted">{emptyLabel}</span>;
    }
    const hiddenCount = persons.length - MAX_VISIBLE_PERSONS;
    const visible = expanded ? persons : persons.slice(0, MAX_VISIBLE_PERSONS);
    return (
        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            {visible.map((person, idx) => (
                <PersonPill key={`${person.name}-${idx}`} person={person} icon={icon} labels={labels} />
            ))}
            {hiddenCount > 0 && (
                <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((prev) => !prev);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="tw-inline-flex tw-items-center tw-gap-1 tw-border tw-border-solid tw-border-navy-a40 tw-rounded-brand tw-px-1.5 tw-py-0.5 tw-bg-navy-a08 hover:tw-bg-navy-a18 tw-text-xs tw-font-semibold tw-text-navy tw-leading-tight tw-cursor-pointer tw-font-inter tw-transition-colors"
                >
                    {expanded ? labels.results.showLessPersons : labels.results.showMorePersons(hiddenCount)}
                </button>
            )}
        </div>
    );
}

function ContactTag({ value, toHref, icon: Icon }) {
    const v = value && String(value).trim() ? String(value).trim() : '';
    if (!v) {
        return <span className="tw-text-sm tw-text-muted">—</span>;
    }
    return (
        <a
            href={toHref(v)}
            onClick={(e) => e.stopPropagation()}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-max-w-full tw-h-6 tw-px-2.5 tw-rounded-full tw-bg-navy-a10 hover:tw-bg-navy-a18 tw-text-navy tw-text-13 tw-font-semibold tw-no-underline tw-transition-colors"
        >
            <Icon size={14} stroke={2} className="tw-flex-shrink-0" aria-hidden />
            <span className="tw-truncate">{v}</span>
        </a>
    );
}

function ResultsRow({
    td,
    zebra,
    locale,
    labels,
    onOpenDetails,
}) {
    const statusKey = normalizeStatus(td.StatusiARBK);
    const meta = statusMeta(statusKey, locale);
    const { muted } = meta;
    const FormIcon = iconForBusinessForm(td.LlojiBiznesit);
    const formLabel = displayOrDash(td.LlojiBiznesit);
    const owners = ownerPersons(td);
    const reps = splitNames(td.Perfaqesuesi);
    const board = splitNames(td.Bordi);
    const showBoard = hasBoard(td);
    const units = unitCount(td);
    const dateLines = statusDateLines(td, statusKey, labels);
    const formChange = td.NdryshimiFormsBiznesit && String(td.NdryshimiFormsBiznesit).trim() ? String(td.NdryshimiFormsBiznesit).trim() : null;
    const [hovered, setHovered] = useState(false);
    const rowBgClass = hovered ? 'tw-bg-hover-neutral' : zebra ? 'tw-bg-navy-a02' : 'tw-bg-paper';
    const hoverHandlers = {
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false),
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenDetails();
        }
    };

    return (
        <React.Fragment>
            <tr
                {...hoverHandlers}
                onClick={onOpenDetails}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                title={labels.results.rowDetails}
                className={`${rowBgClass} tw-cursor-pointer`}
            >
                <th
                    scope="row"
                    className={`${CELL_BASE} tw-text-left tw-font-bold tw-border-0 ${statusKey === 'unknown' ? 'tw-text-ink' : ''}`}
                    style={statusKey === 'unknown' ? undefined : { color: meta.color }}
                >
                    <span className="tw-inline-flex tw-items-center tw-gap-2">
                        <span
                            title={formLabel}
                            aria-label={formLabel}
                            className="tw-inline-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-7 tw-h-7 tw-rounded-brand-sm tw-bg-navy tw-text-white tw-border-2 tw-border-solid tw-border-navy-dark"
                        >
                            <FormIcon size={16} stroke={1.75} aria-hidden />
                        </span>
                        {displayOrDash(td.EmriBiznesit)}
                    </span>
                </th>
                <td className={`${CELL_BASE} tw-border-0 ${cellTextClass(muted)}`}>
                    {displayOrDash(td.EmriTregtar)}
                </td>
                <td className={`${CELL_BASE} tw-border-0 tw-whitespace-nowrap ${cellTextClass(muted)}`}>
                    {String(td.NUI || td.NumriBiznesit || '').trim() ? (
                        <span className="tw-inline-flex tw-items-center tw-h-6 tw-px-2.5 tw-rounded-full tw-border tw-border-solid tw-border-navy-a40 tw-bg-navy-a08 tw-text-navy tw-font-semibold tw-text-13">
                            {String(td.NUI || td.NumriBiznesit).trim()}
                        </span>
                    ) : (
                        '—'
                    )}
                </td>
                <td className={`${CELL_BASE} tw-border-0 tw-whitespace-nowrap ${cellTextClass(muted)}`}>
                    {displayOrDash(td.Komuna)}
                </td>
                <td className={`${CELL_BASE} tw-border-0 ${cellTextClass(muted)}`}>
                    {displayOrDash(td.LlojiBiznesit)}
                </td>
                <td className={`${CELL_BASE} tw-border-0 tw-whitespace-nowrap tw-text-center ${cellTextClass(muted)}`}>
                    {td.TotalNjesite != null && String(td.TotalNjesite).trim() !== '' ? String(td.TotalNjesite) : '—'}
                </td>
                <td className={`${CELL_BASE} tw-border-0`}>
                    <div className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-pb-0.5">
                        <StatusChip statusKey={statusKey} locale={locale} rawLabel={td.StatusiARBK} />
                        {dateLines.map((d) => (
                            <span key={d.label} className="tw-text-xs tw-font-medium tw-text-muted tw-break-words tw-max-w-full">
                                {`${d.label}: ${d.value}`}
                            </span>
                        ))}
                    </div>
                </td>
            </tr>
            <tr
                {...hoverHandlers}
                onClick={onOpenDetails}
                className={`${rowBgClass} tw-cursor-pointer`}
            >
                <td
                    colSpan={7}
                    className="tw-px-2 md:tw-px-3 tw-pt-0 tw-pb-2.5 tw-border-0 tw-border-b-2 tw-border-solid tw-border-line"
                >
                    <div className="tw-relative tw-p-2 md:tw-p-2.5 tw-rounded-brand-md tw-bg-paper">
                        <AccentOverlay color={meta.color} />
                        {units && (
                            <div className="tw-mb-2">
                                <UnitCountChip count={units} labels={labels} />
                            </div>
                        )}
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-2 md:tw-gap-2.5">
                            <div>
                                <GroupHeading>{labels.results.groupOwners}</GroupHeading>
                                <PersonList
                                    persons={owners}
                                    icon={IconUser}
                                    emptyLabel={labels.results.noOwners}
                                    labels={labels}
                                />
                            </div>
                            <div>
                                <GroupHeading>{labels.results.groupReps}</GroupHeading>
                                <PersonList
                                    persons={reps}
                                    icon={IconUsers}
                                    emptyLabel={labels.results.noReps}
                                    labels={labels}
                                />
                            </div>
                            {showBoard && (
                                <div>
                                    <GroupHeading>{labels.results.groupBordi}</GroupHeading>
                                    <PersonList
                                        persons={board}
                                        icon={IconUsers}
                                        emptyLabel={labels.results.noBordi}
                                        labels={labels}
                                    />
                                </div>
                            )}
                            <div>
                                <GroupHeading>{labels.results.numriPuntoreve}</GroupHeading>
                                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-6 tw-px-2.5 tw-rounded-full tw-bg-navy-a10 tw-text-navy tw-text-13 tw-font-semibold">
                                    <IconUsers size={14} stroke={2} aria-hidden />
                                    {displayOrDash(td.NumriPunetoreve)}
                                </span>
                            </div>
                            <div>
                                <GroupHeading>{labels.results.groupContact}</GroupHeading>
                                <ContactTag value={td.Telefoni} toHref={(v) => `tel:${v}`} icon={IconPhone} />
                            </div>
                            <div>
                                <GroupHeading>{labels.results.email}</GroupHeading>
                                <ContactTag value={td.Email} toHref={(v) => `mailto:${v}`} icon={IconMail} />
                            </div>
                            <div>
                                <GroupHeading>{labels.results.sektori}</GroupHeading>
                                <span className="tw-text-sm tw-text-ink tw-break-words">{displayOrDash(td.Sektori)}</span>
                            </div>
                            {formChange && (
                                <div className="sm:tw-col-span-2 md:tw-col-span-3">
                                    <GroupHeading>{labels.results.ndryshimiTipit}</GroupHeading>
                                    <span className="tw-text-sm tw-text-ink">{formChange}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </td>
            </tr>
        </React.Fragment>
    );
}

function ResultCard({
    td,
    locale,
    labels,
    onOpenDetails,
}) {
    const statusKey = normalizeStatus(td.StatusiARBK);
    const meta = statusMeta(statusKey, locale);
    const { muted } = meta;
    const FormIcon = iconForBusinessForm(td.LlojiBiznesit);
    const owners = ownerPersons(td);
    const reps = splitNames(td.Perfaqesuesi);
    const board = splitNames(td.Bordi);
    const showBoard = hasBoard(td);
    const units = unitCount(td);
    const dateLines = statusDateLines(td, statusKey, labels);
    const formChange = td.NdryshimiFormsBiznesit && String(td.NdryshimiFormsBiznesit).trim() ? String(td.NdryshimiFormsBiznesit).trim() : null;
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenDetails();
        }
    };
    const hasTradeName = td.EmriTregtar && String(td.EmriTregtar).trim();
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onOpenDetails}
            onKeyDown={handleKeyDown}
            title={labels.results.rowDetails}
            className="tw-bg-paper tw-border tw-border-solid tw-border-line tw-rounded-brand-lg tw-shadow-card tw-p-3.5 tw-cursor-pointer tw-flex tw-flex-col tw-gap-2.5"
        >
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                <span className="tw-inline-flex tw-items-center tw-gap-2 tw-min-w-0">
                    <span
                        className="tw-inline-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-7 tw-h-7 tw-rounded-brand-sm tw-bg-navy tw-text-white tw-border-2 tw-border-solid tw-border-navy-dark"
                        title={displayOrDash(td.LlojiBiznesit)}
                        aria-label={displayOrDash(td.LlojiBiznesit)}
                    >
                        <FormIcon size={16} stroke={1.75} aria-hidden />
                    </span>
                    <span
                        className={`tw-font-bold tw-text-sm tw-break-words tw-min-w-0 ${statusKey === 'unknown' ? 'tw-text-ink' : ''}`}
                        style={statusKey === 'unknown' ? undefined : { color: meta.color }}
                    >
                        {displayOrDash(td.EmriBiznesit)}
                    </span>
                </span>
                <span className="tw-flex-shrink-0">
                    <StatusChip statusKey={statusKey} locale={locale} rawLabel={td.StatusiARBK} />
                </span>
            </div>

            {hasTradeName && (
                <div className={`tw-text-13 ${cellTextClass(muted)}`}>{displayOrDash(td.EmriTregtar)}</div>
            )}

            <div className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-text-13">
                <span>
                    <span className="tw-text-muted">{`${labels.results.columns.nui}: `}</span>
                    <span className="tw-text-ink tw-font-semibold">{displayOrDash(td.NUI || td.NumriBiznesit)}</span>
                </span>
                <span>
                    <span className="tw-text-muted">{`${labels.results.columns.komuna}: `}</span>
                    <span className={cellTextClass(muted)}>{displayOrDash(td.Komuna)}</span>
                </span>
                <span>
                    <span className="tw-text-muted">{`${labels.results.columns.llojiBiznesit}: `}</span>
                    <span className={cellTextClass(muted)}>{displayOrDash(td.LlojiBiznesit)}</span>
                </span>
                <span>
                    <span className="tw-text-muted">{`${labels.results.columns.njesite}: `}</span>
                    <span className={cellTextClass(muted)}>{td.TotalNjesite != null && String(td.TotalNjesite).trim() !== '' ? String(td.TotalNjesite) : '—'}</span>
                </span>
                {dateLines.map((d) => (
                    <span key={d.label}>
                        <span className="tw-text-muted">{`${d.label}: `}</span>
                        <span className="tw-text-ink">{d.value}</span>
                    </span>
                ))}
            </div>

            {units && <UnitCountChip count={units} labels={labels} />}

            <div className="tw-relative tw-rounded-brand-md tw-p-2 tw-bg-paper">
                <AccentOverlay color={meta.color} />
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2">
                    <div>
                        <GroupHeading>{labels.results.groupOwners}</GroupHeading>
                        <PersonList persons={owners} icon={IconUser} emptyLabel={labels.results.noOwners} labels={labels} />
                    </div>
                    <div>
                        <GroupHeading>{labels.results.groupReps}</GroupHeading>
                        <PersonList persons={reps} icon={IconUsers} emptyLabel={labels.results.noReps} labels={labels} />
                    </div>
                    {showBoard && (
                        <div>
                            <GroupHeading>{labels.results.groupBordi}</GroupHeading>
                            <PersonList persons={board} icon={IconUsers} emptyLabel={labels.results.noBordi} labels={labels} />
                        </div>
                    )}
                    <div>
                        <GroupHeading>{labels.results.numriPuntoreve}</GroupHeading>
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-6 tw-px-2.5 tw-rounded-full tw-bg-navy-a10 tw-text-navy tw-text-13 tw-font-semibold">
                            <IconUsers size={14} stroke={2} aria-hidden />
                            {displayOrDash(td.NumriPunetoreve)}
                        </span>
                    </div>
                    <div>
                        <GroupHeading>{labels.results.groupContact}</GroupHeading>
                        <ContactTag value={td.Telefoni} toHref={(v) => `tel:${v}`} icon={IconPhone} />
                    </div>
                    <div>
                        <GroupHeading>{labels.results.email}</GroupHeading>
                        <ContactTag value={td.Email} toHref={(v) => `mailto:${v}`} icon={IconMail} />
                    </div>
                    <div>
                        <GroupHeading>{labels.results.sektori}</GroupHeading>
                        <span className="tw-text-sm tw-text-ink tw-break-words">{displayOrDash(td.Sektori)}</span>
                    </div>
                    {formChange && (
                        <div className="tw-col-span-full">
                            <GroupHeading>{labels.results.ndryshimiTipit}</GroupHeading>
                            <span className="tw-text-sm tw-text-ink">{formChange}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const TableSearch = (props) => {
    const searchData = useSelector((state) => state.tableSearch.tableList);
    const dispatch = useDispatch();
    const history = useHistory();
    const { locale } = props;
    const labels = getLabels(locale);

    // null = renditja e API-se (perputhja me e mire: emri qe fillon me fjalen e kerkuar del i pari).
    // Renditja alfabetike ketu e prishte ate, ndaj aktivizohet vetem kur perdoruesi zgjedh kolonen.
    const [orderBy, setOrderBy] = useState(null);
    const [order, setOrder] = useState('asc');

    const rows = useMemo(
        () => (Array.isArray(searchData) ? searchData : [])
            // Bashkojme DetajetToken (token i nenshkruar per te hapur detajet) dhe PronaretLista
            // (emri + shteti + lloji i personit) me te dhenat e biznesit.
            .map((item) => (item.teDhenatBiznesit
                ? {
                    ...item.teDhenatBiznesit,
                    DetajetToken: item.DetajetToken,
                    PronaretLista: item.PronaretLista,
                }
                : null))
            .filter(Boolean),
        [searchData],
    );

    const sortedRows = useMemo(() => {
        if (!orderBy) return rows;
        const dir = order === 'asc' ? 1 : -1;
        return rows
            .map((td, index) => ({ td, index }))
            .sort((a, b) => {
                const av = sortValue(a.td, orderBy);
                const bv = sortValue(b.td, orderBy);
                if (av < bv) return -1 * dir;
                if (av > bv) return 1 * dir;
                return a.index - b.index;
            })
            .map((entry) => entry.td);
    }, [rows, orderBy, order]);

    // Kolona: rritese -> zbritese -> renditja e API-se.
    const handleSort = (key) => {
        if (orderBy === key) {
            if (order === 'asc') {
                setOrder('desc');
            } else {
                setOrderBy(null);
                setOrder('asc');
            }
        } else {
            setOrderBy(key);
            setOrder('asc');
        }
    };

    const openDetails = (td) => {
        // Ruajme tokenin e nenshkruar (jo nRegjistriID te papertecktuar) -> mbyll enumerimin.
        dispatch(updateTableDetailsID(td.DetajetToken));
        history.push('/TableDetails');
    };

    const shown = rows.length;
    const total = Math.max(Number(rows[0]?.TotalRows || 0), shown);
    const showCountBanner = shown > 0;

    const columns = [
        { key: 'EmriBiznesit', label: labels.results.columns.emri },
        { key: 'EmriTregtar', label: labels.results.columns.emriTregtar },
        { key: 'NUI', label: labels.results.columns.nui },
        { key: 'Komuna', label: labels.results.columns.komuna },
        { key: 'LlojiBiznesit', label: labels.results.columns.llojiBiznesit },
        { key: 'TotalNjesite', label: labels.results.columns.njesite },
        { key: 'Statusi', label: labels.results.columns.statusi },
    ];

    return (
        <div className="TableSearch-section">
            <PageTitle header={<FormattedMessage id="tableSearch.title" defaultMessage="Rezultatet e Bizneseve" />} />
            <section aria-label={labels.results.sectionTitle} className="tw-font-inter tw-mt-2">
                {showCountBanner && (
                    <div
                        role="status"
                        className="tw-flex tw-items-center tw-gap-3 tw-mb-4 tw-px-5 tw-py-2.5 tw-rounded-brand tw-bg-banner tw-shadow-banner tw-text-ink tw-text-sm tw-font-medium tw-leading-relaxed"
                        style={{ border: '3px double rgba(14, 58, 94, 0.55)' }}
                    >
                        <IconInfoCircle size={20} className="tw-text-navy tw-flex-shrink-0" aria-hidden />
                        <span>
                            {labels.results.countBanner(shown, total)}
                            {total > shown && ` ${labels.results.refineHint}`}
                        </span>
                    </div>
                )}

                {shown === 0 ? (
                    <div
                        role="status"
                        className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-py-3 tw-rounded-brand tw-border tw-border-solid tw-text-sm"
                        style={{ borderColor: '#03A9F4', color: '#014361' }}
                    >
                        <IconInfoCircle size={20} className="tw-flex-shrink-0" style={{ color: '#0288D1' }} aria-hidden />
                        <span>{labels.results.empty}</span>
                    </div>
                ) : (
                    <React.Fragment>
                    <div className="tw-hidden md:tw-block tw-bg-paper tw-border tw-border-solid tw-border-line tw-rounded-brand-lg tw-overflow-x-auto">
                        <table className="tw-w-full tw-table-fixed tw-border-collapse tw-font-inter" aria-label={labels.results.sectionTitle}>
                            <thead>
                                <tr>
                                    {columns.map((col) => {
                                        const active = orderBy === col.key;
                                        return (
                                            <th
                                                key={col.key}
                                                aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                                                className="tw-bg-navy tw-text-white tw-font-bold tw-text-sm tw-whitespace-nowrap tw-text-left tw-px-4 tw-py-3.5 tw-border-0 tw-border-b tw-border-solid tw-border-navy-dark"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => handleSort(col.key)}
                                                    className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-0 tw-p-0 tw-text-white tw-font-bold tw-text-sm tw-cursor-pointer tw-font-inter"
                                                >
                                                    {col.label}
                                                    <span
                                                        aria-hidden
                                                        className={`tw-inline-flex tw-transition-transform ${active ? 'tw-opacity-100' : 'tw-opacity-40'}`}
                                                        style={{ transform: active && order === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)' }}
                                                    >
                                                        <IconArrowUp size={15} stroke={2.25} />
                                                    </span>
                                                </button>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((td, index) => (
                                    <ResultsRow
                                        key={`${td.nRegjistriID || td.NumriBiznesit || td.EmriBiznesit}-${index}`}
                                        td={td}
                                        zebra={index % 2 === 1}
                                        locale={locale}
                                        labels={labels}
                                        onOpenDetails={() => openDetails(td)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="tw-flex md:tw-hidden tw-items-center tw-gap-2 tw-mb-3">
                        <label
                            htmlFor="arbk-mobile-sort"
                            className="tw-text-13 tw-font-semibold tw-text-muted tw-whitespace-nowrap"
                        >
                            {labels.results.sortLabel}
                        </label>
                        <select
                            id="arbk-mobile-sort"
                            value={orderBy || ''}
                            onChange={(e) => setOrderBy(e.target.value || null)}
                            className="tw-flex-1 tw-min-w-0 tw-h-9 tw-px-2 tw-rounded-brand tw-border tw-border-solid tw-border-line tw-bg-paper tw-text-13 tw-text-ink tw-font-inter"
                        >
                            <option value="">{labels.results.sortRelevance}</option>
                            {columns.map((col) => (
                                <option key={col.key} value={col.key}>
                                    {col.label}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                            disabled={!orderBy}
                            aria-label={order === 'asc' ? 'A → Z' : 'Z → A'}
                            className="disabled:tw-opacity-40 disabled:tw-cursor-default tw-inline-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-9 tw-h-9 tw-rounded-brand tw-border tw-border-solid tw-border-line tw-bg-paper tw-text-navy tw-cursor-pointer"
                        >
                            <span
                                className="tw-inline-flex"
                                style={{ transform: order === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)' }}
                            >
                                <IconArrowUp size={16} stroke={2.25} />
                            </span>
                        </button>
                    </div>
                    <div className="tw-grid md:tw-hidden tw-grid-cols-1 tw-gap-3">
                        {sortedRows.map((td, index) => (
                            <ResultCard
                                key={`card-${td.nRegjistriID || td.NumriBiznesit || td.EmriBiznesit}-${index}`}
                                td={td}
                                locale={locale}
                                labels={labels}
                                onOpenDetails={() => openDetails(td)}
                            />
                        ))}
                    </div>
                    </React.Fragment>
                )}
            </section>
        </div>
    );
};

const mapStateToProps = (state) => ({
    locale: state.locale,
});

export default connect(mapStateToProps)(TableSearch);
